import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse} from '@angular/common/http';
import {Observable, Subscription, of, throwError} from 'rxjs';
import LoginDto from '../model/auth/login.dto';
import {catchError, tap, switchMap, finalize, shareReplay, map} from 'rxjs/operators';
import {JwtHelperService} from '@auth0/angular-jwt';
import {LoginResponse} from '../model/auth/login-response';
import {PrincipalUser} from '../model/auth/principal-user';
import {Router} from '@angular/router';
import {clearAllCustomFieldKeys, clearAllDynamicIndicatorNormalUserKeys,clearAllGroupKeys, clearAllLocationDatsetItemKeys, UserStateService} from './user-state-service';
import {ChangePasswordRequest} from '../model/user/change-password-request';
import { environment } from 'src/environments/environment';
import { Constants } from '../constants';
import {now} from 'moment';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private readonly TOKEN_STORAGE_KEY = 'access_token';
    private readonly REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';
    private readonly PRINCIPAL_USER = 'principalUser';
    private readonly AUTH_PROVIDER_KEY = 'auth_provider';

    principal: PrincipalUser;
    private refreshTimerSub: Subscription | null = null;
    private isRefreshing = false;
    private refreshInFlight$: Observable<LoginResponse> | null = null;
    private refreshTimeout: any;

    public get refreshing(): boolean {
        return this.isRefreshing;
    }


    // only used when a user first needs to activate 2FA
    // necessary because call the login API after activating the 2FA, so we need to store the login details somewhere
    temporaryLoginRequest: LoginDto;

    constructor(
        private readonly http: HttpClient,
        private readonly jwtHelperService: JwtHelperService,
        private readonly router: Router,
        private readonly userStateService: UserStateService,
        private readonly dialog: MatDialog
    ) {
        const existingToken = localStorage.getItem(this.TOKEN_STORAGE_KEY);
        if (existingToken) {
            this.principal = this.decodeToken(existingToken);
        }
    }

    getPrincipal() {
        return this.principal;
    }

    setTemporaryLoginRequest(request: LoginDto){
        this.temporaryLoginRequest = request;
    }

    login(loginRequest: LoginDto): Observable<LoginResponse> {
        return this.http.post<LoginResponse>(
            `/login`,
            loginRequest,
            {
                withCredentials: true
            }
        ).pipe(
            tap(response => {
                this.temporaryLoginRequest = null;
                this.setupAuthenticationDetails(response)
            })
        );
    }

    loginUsingGroupPermissionToken(token: string, groupId: number): Observable<LoginResponse> {
        return this.http.post<LoginResponse>(
            `/login/groups/${groupId}/token/${token}`,
            {}
        );
    }

    confirmSsoToken(token: string): Observable<LoginResponse> {
        console.log('calling confirm endpoint: /saml/confirm');
        return this.http.post<LoginResponse>(
            `/saml/confirm?token=${token}`,
            null
        ).pipe(
            tap(response => {
                this.setupAuthenticationDetails(response);
            })
        );
    }

    getHomePageForUser(): string {
        if (this.principal?.provider) {
            return '/private/overlays/api-meter';
        }
        return '/private/account';
    }

    public setupAuthenticationDetails(response) {
        this.principal = response.principalUser;//this.decodeToken(response.token);
        try {
            // localStorage.setItem(this.TOKEN_STORAGE_KEY, response.token);
            localStorage.setItem(this.TOKEN_STORAGE_KEY, response.accessToken);
            localStorage.setItem(this.REFRESH_TOKEN_STORAGE_KEY, response.refreshToken);
            localStorage.setItem(this.PRINCIPAL_USER, JSON.stringify(response.principalUser));

            // Store authProvider for multi-tab logout handling - persists across logout
            if (response.principalUser?.authProvider) {
                localStorage.setItem(this.AUTH_PROVIDER_KEY, response.principalUser.authProvider);
            }

            this.userStateService.initialize(this.principal);

            this.scheduleTokenRefresh(response.accessToken);
        } catch (e) {
            console.error(e);
        }
    }

    decodeToken(token: string): any {
        const decodedToken = this.jwtHelperService.decodeToken(token);
        return JSON.parse(localStorage.getItem(this.PRINCIPAL_USER)); // JSON.parse(decodedToken.principal);
    }

    logout(callLogoutApi: boolean = false): void {
        // Read from localStorage instead of in-memory principal (handles multi-tab scenario)
        const authProvider = localStorage.getItem(this.AUTH_PROVIDER_KEY);

        this.cleanAuthenticationDetails(callLogoutApi);

        // Redirect based on authentication provider
        if (authProvider === 'AZURE') {
            // Redirect to Azure logout URL
            window.location.href = environment.azureLogout;
        } else {
            // Standard logout - redirect to login page
            this.router.navigate(['/public/login']);
        }
    }

    forgotPassword(email: string): Observable<void> {
        let request = {email: email};
        return this.http.post<void>('/forgot-password', request);
    }

    confirmForgotPassword(token: string, password: string, confirmPassword: string): Observable<void> {
        let request = {password: password, confirmPassword: confirmPassword};
        return this.http.post<void>(`/forgot-password/confirm/${token}`, request);
    }

    validateForgotPasswordToken(token: string): Observable<boolean> {
        return this.http.post<boolean>(`/forgot-password/confirm/validate/${token}`, {});
    }


    cleanAuthenticationDetails(callLogoutApi: boolean = false): void {
        this.principal = null;
        this.temporaryLoginRequest = null;
        
        if (this.dialog.openDialogs && this.dialog.openDialogs.length > 0) {
            this.dialog.closeAll();
        }
        
        if(callLogoutApi) {
            this.logoutCall().subscribe(response => {});
        }
        clearAllDynamicIndicatorNormalUserKeys();
        localStorage.removeItem(this.TOKEN_STORAGE_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_STORAGE_KEY);
        localStorage.removeItem(this.PRINCIPAL_USER);
        localStorage.removeItem('isAzureUser');
        localStorage.removeItem('_grecaptcha');
        clearAllGroupKeys();
        clearAllCustomFieldKeys()
        clearAllLocationDatsetItemKeys()
        this.userStateService.clearAll();
        if (this.refreshTimerSub) {
            this.refreshTimerSub.unsubscribe();
            this.refreshTimerSub = null;
        }
    }

    logoutCall(): Observable<any> {
        return this.http.post<void>('/logout', {});
    }

    verifyInvitationToken(token: string): Observable<boolean> {
        return this.http.get<boolean>(`/members/verify/${token}`);
    }

    acceptMemberInvitation(token: string, name: string, password: string): Observable<any> {
        return this.http.post('/members/accept', {token, name, password});
    }

    isAuthenticated(): boolean {
        const token = localStorage.getItem(this.TOKEN_STORAGE_KEY);
        if (!token) return false;
        try {
            return !this.jwtHelperService.isTokenExpired(token);
        } catch {
            return false;
        }
    }

    public getToken(): string {
        return localStorage.getItem(this.TOKEN_STORAGE_KEY);
    }

    public getRefreshToken(): Observable<LoginResponse> {
        // Return the in-flight observable so all concurrent callers share one HTTP call
        if (this.isRefreshing && this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        const refreshToken = this.getExistingRefreshToken();
        if (!refreshToken) return throwError(() => 'No refresh token found');

        this.isRefreshing = true;
        this.refreshInFlight$ = this.http.post<LoginResponse>(`/refresh-token/${refreshToken}`, {})
            .pipe(
                tap(response => {
                    this.isRefreshing = false;
                    this.refreshInFlight$ = null;
                    this.setupAuthenticationDetails(response);
                }),
                catchError(err => {
                    this.isRefreshing = false;
                    this.refreshInFlight$ = null;
                    // The only place a failed refresh ends the session - callers must not log out again
                    if (!this.isTransientRefreshError(err)) {
                        this.logout();
                    }
                    return throwError(() => err);
                }),
                shareReplay(1)
            );

        return this.refreshInFlight$;
    }

    /**
     * A refresh that failed because the server could not be reached or was restarting (network error,
     * 502/503/504) says nothing about the refresh token, so it must not end the session - the next
     * request simply tries again. Anything else is treated as a rejected token, including 500: that is
     * what the backend currently returns for a stale refresh token, as InvalidTokenException has no
     * handler of its own.
     */
    private isTransientRefreshError(err: any): boolean {
        return err instanceof HttpErrorResponse && [0, 502, 503, 504].includes(err.status);
    }

    public getExistingRefreshToken(): string {
        return localStorage.getItem(this.REFRESH_TOKEN_STORAGE_KEY);
    }

    /**
     * For route guards: emits true when the access token is still valid, or once an expired one has
     * been refreshed. Logs out only when there is no refresh token or the refresh itself fails.
     *
     * An expired access token on its own is not a reason to log out - after a page reload no refresh
     * is scheduled, so the token routinely expires while the user is idle.
     */
    ensureAuthenticated(): Observable<boolean> {
        if (this.isAuthenticated()) {
            return of(true);
        }
        if (!this.getExistingRefreshToken()) {
            this.logout();
            return of(false);
        }
        return this.getRefreshToken().pipe(
            map(() => true),
            // getRefreshToken() has already logged out if the token was rejected
            catchError(() => of(false))
        );
    }

    changeUserPassword(request: ChangePasswordRequest): Observable<void> {
        return this.http.put<void>(`/changePassword`, request);
    }

    can(action: string, accountID?: number) {
        if(this.principal && this.principal.isSuperadmin == undefined) {
            return false;
        }
        let role = this.principal.isSuperadmin ? 'admin' : 'user';
        let isSuperAdmin = role === 'admin';
        if (isSuperAdmin) {
            return true;
        }
        let isMember = this.principal.membership.map((account) => account.accountID).includes(accountID);
        let isAccountAdmin = false;
        try {
            isAccountAdmin = this.principal.membership.find((account) => {
                return account.accountID === accountID;
            }).isAccountAdmin;
        } catch (err) {
            // console.log(err);
        }
        switch (action) {
            case 'read': {
                if (isMember) {
                    return true;
                } else {
                    return false;
                }
            }
            case 'update': {
                if (isMember && isAccountAdmin) {
                    return true;
                } else {
                    return false;

                }
            }
            case 'delete': {
                if (isSuperAdmin) {
                    return true;
                } else {
                    return false;
                }
            }
            case 'create': {
                if (isSuperAdmin) {
                    return true;
                } else {
                    return false;
                }
            }
            default: {
                return false;
            }
        }
    }

    /**
     * JIRA-EMFDU-001 - whether this user may run the ingestion workflow in the given account:
     * upload, drafts, geocoding, linking, approval, publishing, and subgroup creation.
     *
     * Mirrors the backend's PrincipalUser.canUpload exactly - superadmin, or account admin, or the
     * EMFD+U (WRITE) permission bit - so the two cannot drift apart. Per the decision of
     * 2026-09-28 there is no separate upload flag: EMFD+U is that same bit, relabelled.
     *
     * Reads principal.membership directly and is therefore synchronous, unlike
     * UserStateService.hasWriteAccess, whose backing map is populated by an async initialize() and
     * can still be empty when a guard or an ngOnInit runs.
     */
    canUpload(accountID?: number): boolean {
        const principal = this.getPrincipal();
        if (!principal) {
            return false;
        }
        if (principal.isSuperadmin) {
            return true;
        }
        if (accountID === null || accountID === undefined) {
            return false;
        }
        const member = principal.membership.find((m) => m.accountID === accountID);
        return !!member && (!!member.isAccountAdmin || member.permissions > 0);
    }

    wpLogoutCall(): Observable<any> {
        return this.http.get<any>(environment.wpLogout);
    }

    scheduleTokenRefresh(token: string) {
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000;
        const refreshDelay = Math.max(expiry - Date.now() - 5_000, 0);

        this.refreshTimeout = setTimeout(() => {
            if (this.isRefreshing) return;
            this.getRefreshToken().subscribe({
                next: () => console.log('Token auto-refreshed'),
                error: (err) => console.error('Auto-refresh failed', err)
            });
        }, refreshDelay);
    }

    // Inject Authorization header using the current access token
    attachAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
        return request.clone({
            setHeaders: {
                Authorization: `${this.getToken()}`
            }
        });
    }

    // Standardized refresh-and-retry flow used by interceptors
    refreshTokenAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.getRefreshToken().pipe(
            switchMap(() => {
                return next.handle(this.attachAuthHeader(request));
            }),
            catchError(error => {
                // getRefreshToken() has already logged out if the token was rejected. Logging out here
                // as well would also end the session on a transient refresh failure, or on any error
                // from the retried request itself.
                return throwError(() => error);
            }),
            finalize(() => {
                // no-op here, getRefreshToken already flips isRefreshing
            })
        );
    }

}
